import React, { useState, useMemo } from 'react';
import { ChevronDown, Gavel, Filter } from 'lucide-react';
import { getInitials, getBranchMeta, formatDate } from '../utils/format';
import styles from './DecisionLog.module.css';

function DecisionItem({ decision }) {
  const [expanded, setExpanded] = useState(false);
  const branchMeta = getBranchMeta(decision.branch);
  const owner = decision.deciding_agent || decision.owner;

  return (
    <article className={styles.item}>
      <button
        type="button"
        className={styles.summary}
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <div className={styles.summaryLeft}>
          <span className={styles.timestamp}>{formatDate(decision.timestamp)}</span>
          {owner && (
            <span className={styles.agentChip}>
              <span
                className={styles.chipAvatar}
                style={{
                  backgroundColor: `${branchMeta.color}20`,
                  color: branchMeta.color,
                }}
              >
                {getInitials(owner.name || owner)}
              </span>
              <span className={styles.chipName}>{owner.name || owner}</span>
            </span>
          )}
          <h3 className={styles.itemTitle}>{decision.summary || decision.decision}</h3>
        </div>
        <ChevronDown
          size={18}
          className={`${styles.toggle} ${expanded ? styles.toggleOpen : ''}`}
        />
      </button>

      {expanded && (
        <div className={styles.details}>
          <div className={styles.reasoning}>
            <h4 className={styles.detailsTitle}>Reasoning</h4>
            <p className={styles.reasoningText}>{decision.reasoning || 'No reasoning provided.'}</p>
          </div>
          {(decision.affected_tasks?.length > 0 || decision.affected_agents?.length > 0) && (
            <div className={styles.affects}>
              <h4 className={styles.detailsTitle}>Affects</h4>
              <div className={styles.affectChips}>
                {(decision.affected_tasks || []).map((task) => (
                  <span key={task} className={styles.affectChip}>
                    #{task}
                  </span>
                ))}
                {(decision.affected_agents || []).map((agent) => (
                  <span key={agent} className={styles.affectChip}>
                    {agent}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export function DecisionLog({ decisions }) {
  const [agentFilter, setAgentFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');

  const branches = useMemo(
    () => Array.from(new Set(decisions.map((d) => d.branch).filter(Boolean))),
    [decisions]
  );
  const agents = useMemo(
    () => Array.from(
      new Set(
        decisions
          .map((d) => d.deciding_agent?.name || d.deciding_agent?.id || d.owner)
          .filter(Boolean)
      )
    ),
    [decisions]
  );

  const filtered = useMemo(() => {
    return decisions.filter((d) => {
      if (agentFilter) {
        const name = d.deciding_agent?.name || d.deciding_agent?.id || d.owner;
        if (name !== agentFilter) return false;
      }
      if (branchFilter && d.branch !== branchFilter) return false;
      return true;
    });
  }, [decisions, agentFilter, branchFilter]);

  return (
    <section className={styles.log} aria-label="Decision log">
      <div className={styles.toolbar}>
        <h2 className={styles.title}>Decision Log</h2>
        <div className={styles.filters}>
          <Filter size={14} className={styles.filterIcon} aria-hidden="true" />
          <select
            aria-label="Filter by agent"
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className={styles.select}
          >
            <option value="">All Agents</option>
            {agents.map((agent) => (
              <option key={agent} value={agent}>{agent}</option>
            ))}
          </select>
          <select
            aria-label="Filter by branch"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className={styles.select}
          >
            <option value="">All Branches</option>
            {branches.map((branch) => (
              <option key={branch} value={branch}>{branch}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.count}>{filtered.length} decisions</div>

      <ul className={styles.list}>
        {filtered.map((decision) => (
          <li key={decision.id}>
            <DecisionItem decision={decision} />
          </li>
        ))}
      </ul>

      {filtered.length === 0 && (
        <div className={styles.empty}>
          <Gavel size={48} className={styles.emptyIcon} aria-hidden="true" />
          <p className={styles.emptyTitle}>No decisions</p>
          <p className={styles.emptySubtitle}>Decisions will be logged here.</p>
        </div>
      )}
    </section>
  );
}
